// src/pages/Admin/Adherents/InscriptionPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import Button from "./../../../components/Button/Button";
import { useNavigate, useLocation } from "react-router-dom";
import useScrollToTop from "./../../../hooks/useScollToTop";
import {
  addNewAdherent,
  getAllAdherentsList,
  updateAdherentPhoto,
} from "./../../../services/adherentServices";
import { assignAdherentToUser } from "./../../../services/userServices";
import { createNewDossierInscription } from "./../../../services/dossierServices";
import {
  sendNotification,
  sendNotificationToAdmin,
} from "./../../../services/notificationServices";
import { getSettings } from "./../../../services/settingsServices";
import { getGroupsList } from "./../../../services/groupServices";
import { getDojos } from "./../../../services/dojoServices";
import { useUser } from "../../Home/UserContext";
import { ToastContainer, toast } from "react-toastify";
import moment from "moment";
import {
  FaPlus,
  FaTimes,
  FaClock,
  FaUserFriends,
  FaEuroSign,
  FaMapPin,
} from "react-icons/fa";
import AdherentsMiniTable from "./AdherentsMiniTable";

// --- Helpers & constantes ---
const formatPrenom = (prenom) =>
  prenom
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("-");

const MAX_IMAGE_SIZE_MB = 6;
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jpg",
];

const InscriptionPage = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { user } = useUser();
  const location = useLocation();
  const groupeIdFromState = location.state?.groupeId;

  const [saisonActive, setSaisonActive] = useState("");

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    genre: "",
    date_naissance: "",
    lieu_naissance: "",
    licence_number: "",
    adresse: "",
    cp: "",
    commune: "",
    representant_legal: "",
    tel: "",
    tel_secondaire: "",
    email: "",
    email_secondaire: "",
    groupeId: "",
    // ✅ Modification 1 : type d'inscription fixé sur "Inscrit" (plus de bouton de sélection)
    statut_inscription: "Inscrit",
    autorisation_photo: true, // droit à l'image (coché = autorisé)
    nationalite_fra: true,
    taille_tshirt: "",
    notes_adherent: "",
    saison_active: "",
    passsport_number: "",
  });

  const [userInfo, setUserInfo] = useState({ nom: "", prenom: "", userId: "" });

  const [groupes, setGroupes] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedGroupIdToAdd, setSelectedGroupIdToAdd] = useState("");
  const [dateError, setDateError] = useState("");
  const [groupError, setGroupError] = useState("");
  const [adherentsList, setAdherentsList] = useState([]);
  const [duplicateWarning, setDuplicateWarning] = useState("");

  // On garde les dojos uniquement pour afficher le nom du dojo dans les options/cartes (sans select de lieu)
  const [dojos, setDojos] = useState([]);

  const [isQuotaActive, setIsQuotaActive] = useState(false);

  // Communes (auto-complétion depuis code postal)
  const [communes, setCommunes] = useState([]);
  const [communeLoading, setCommuneLoading] = useState(false);

  // === Photo obligatoire ===
  const [selectedPhoto, setSelectedPhoto] = useState(null); // File
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoError, setPhotoError] = useState("");

  // ====== Effects ======
  useEffect(() => {
    (async () => {
      try {
        const settingsData = await getSettings();
        const isqa =
          settingsData?.isquotaactive ??
          settingsData?.[0]?.isquotaactive ??
          false;
        setIsQuotaActive(!!isqa);

        const saison =
          settingsData?.[0]?.saison_active || settingsData?.saison_active || "";
        setSaisonActive(saison);
        setFormData((prev) => ({
          ...prev,
          saison_active: saison,
          statut_inscription: "Inscrit", // force (au cas où)
        }));
      } catch (e) {
        console.error("Erreur settings:", e);
      }
    })();
  }, []);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("userInfo"));
    if (stored) {
      setUserInfo({
        nom: stored.nom || "",
        prenom: stored.prenom || "",
        userId: stored.userId || "",
      });
    }
  }, []);

  // ✅ Modification 2 : Groupes = tous les groupes actifs, sans filtre par dojo
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await getGroupsList();
        const activeGroups = (response || []).filter((g) => g.is_active);
        if (!mounted) return;
        setGroupes(activeGroups);

        // Pré-sélection depuis state nav
        if (groupeIdFromState) {
          const selected = activeGroups.find(
            (g) => g.id.toString() === groupeIdFromState.toString(),
          );
          if (selected) {
            setSelectedGroupIdToAdd(String(selected.id));
          }
        }
      } catch (e) {
        console.error("Erreur chargement groupes:", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [groupeIdFromState]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getDojos();
        setDojos(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur dojos:", e);
        toast.error("Erreur lors de la récupération des dojos");
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const adherents = await getAllAdherentsList();
        setAdherentsList(Array.isArray(adherents) ? adherents : []);
      } catch (e) {
        console.error("Erreur liste adhérents:", e);
      }
    })();
  }, []);

  // Communes depuis CP
  useEffect(() => {
    const cp = formData.cp;
    if (!/^\d{5}$/.test(cp)) {
      setCommunes([]);
      setCommuneLoading(false);
      return;
    }

    let aborted = false;
    setCommuneLoading(true);
    fetch(
      `https://geo.api.gouv.fr/communes?codePostal=${cp}&fields=nom&format=json`,
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (aborted) return;
        const list = Array.isArray(data)
          ? data
              .map((c) => (c?.nom || "").toUpperCase())
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b, "fr"))
          : [];
        setCommunes(list);
        if (list.length === 1) {
          setFormData((prev) => ({ ...prev, commune: list[0] }));
        }
      })
      .catch(() => {
        if (!aborted) setCommunes([]);
      })
      .finally(() => {
        if (!aborted) setCommuneLoading(false);
      });

    return () => {
      aborted = true;
    };
  }, [formData.cp]);

  // ====== Utils ======
  const isGroupFull = (g) =>
    Number(g?.nombreAdherents || 0) >= Number(g?.quota_groupe || 0);

  const checkForDuplicate = (nom, prenom) => {
    const duplicate = adherentsList.find(
      (a) => a.nom === nom && a.prenom === prenom,
    );
    setDuplicateWarning(
      duplicate
        ? `Un adhérent avec le nom ${nom} et le prénom ${prenom} existe déjà.`
        : "",
    );
  };

  const validerEtFormatterDate = (dateStr) => {
    const cleaned = String(dateStr || "").replace(/[^0-9/]/g, "");
    const formats = ["DD/MM/YY", "DDMMYY", "DDMMYYYY", "DD/MM/YYYY"];
    for (let f of formats) {
      const m = moment(cleaned, f, true);
      if (m.isValid()) return m.format("YYYY-MM-DD");
    }
    return null;
  };

  const euro = (n) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(Number(n) || 0);

  const dojoById = useMemo(() => {
    const map = {};
    for (const d of dojos) map[d.id] = d;
    return map;
  }, [dojos]);

  // Palette pour jauges/groupes (selon g.color_groupe)
  const COLOR_TOKEN_MAP = {
    "bg-orange-400": {
      light: "bg-orange-200",
      solid: "bg-orange-500",
      border: "border-orange-300",
    },
    "bg-indigo-400": {
      light: "bg-indigo-200",
      solid: "bg-indigo-500",
      border: "border-indigo-300",
    },
    "bg-purple-400": {
      light: "bg-purple-200",
      solid: "bg-purple-500",
      border: "border-purple-300",
    },
    "bg-green-400": {
      light: "bg-green-200",
      solid: "bg-green-500",
      border: "border-green-300",
    },
    "bg-yellow-400": {
      light: "bg-yellow-200",
      solid: "bg-yellow-500",
      border: "border-yellow-300",
    },
    "bg-blue-400": {
      light: "bg-blue-200",
      solid: "bg-blue-500",
      border: "border-blue-300",
    },
    "bg-red-400": {
      light: "bg-red-200",
      solid: "bg-red-500",
      border: "border-red-300",
    },
    "bg-pink-400": {
      light: "bg-pink-200",
      solid: "bg-pink-500",
      border: "border-pink-300",
    },
    "bg-gray-400": {
      light: "bg-gray-200",
      solid: "bg-gray-500",
      border: "border-gray-300",
    },
    "bg-cyan-400": {
      light: "bg-cyan-200",
      solid: "bg-cyan-500",
      border: "border-cyan-300",
    },
    "bg-teal-400": {
      light: "bg-teal-200",
      solid: "bg-teal-500",
      border: "border-teal-300",
    },
    "bg-emerald-400": {
      light: "bg-emerald-200",
      solid: "bg-emerald-500",
      border: "border-emerald-300",
    },
    "bg-lime-400": {
      light: "bg-lime-200",
      solid: "bg-lime-500",
      border: "border-lime-300",
    },
    "bg-amber-400": {
      light: "bg-amber-200",
      solid: "bg-amber-500",
      border: "border-amber-300",
    },
    "bg-rose-400": {
      light: "bg-rose-200",
      solid: "bg-rose-500",
      border: "border-rose-300",
    },
    "bg-violet-400": {
      light: "bg-violet-200",
      solid: "bg-violet-500",
      border: "border-violet-300",
    },
    "bg-sky-400": {
      light: "bg-sky-200",
      solid: "bg-sky-500",
      border: "border-sky-300",
    },
    "bg-fuchsia-400": {
      light: "bg-fuchsia-200",
      solid: "bg-fuchsia-500",
      border: "border-fuchsia-300",
    },
    "bg-zinc-400": {
      light: "bg-zinc-200",
      solid: "bg-zinc-500",
      border: "border-zinc-300",
    },
    "bg-gray-500": {
      light: "bg-gray-300",
      solid: "bg-gray-600",
      border: "border-gray-400",
    },
  };
  const getLightColorClass = (c) => COLOR_TOKEN_MAP[c]?.light || "bg-slate-200";
  const getSolidColorClass = (c) => COLOR_TOKEN_MAP[c]?.solid || "bg-slate-400";
  const getBorderColorClass = (c) =>
    COLOR_TOKEN_MAP[c]?.border || "border-slate-300";

  // ====== Handlers ======
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let formattedValue = type === "checkbox" ? checked : value;

    if (name === "tel" || name === "tel_secondaire") {
      formattedValue = String(value).replace(/\D/g, "");
    } else if (name === "nom") {
      formattedValue = String(value)
        .toUpperCase()
        .replace(/[^A-Z\s-]/g, "");
    } else if (name === "prenom") {
      formattedValue = formatPrenom(
        String(value).replace(/[^a-zA-ZÀ-ÿ\s-]/g, ""),
      );
    } else if (name === "commune") {
      formattedValue = String(value).toUpperCase();
    } else if (name === "cp") {
      formattedValue = String(value).replace(/\D/g, "").slice(0, 5);
      setCommunes([]);
      setFormData((prev) => ({ ...prev, cp: formattedValue, commune: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));

    if (name === "nom" || name === "prenom") {
      checkForDuplicate(
        name === "nom" ? formattedValue : formData.nom,
        name === "prenom" ? formattedValue : formData.prenom,
      );
    }
  };

  const handleDateChange = (e) => {
    let { value } = e.target;
    let formattedValue = String(value).replace(/[^\d/]/g, "");

    if (formattedValue.length === 2 || formattedValue.length === 5) {
      if (formattedValue.slice(-1) !== "/") {
        formattedValue += "/";
      }
    }
    if (formattedValue.length > 10) {
      formattedValue = formattedValue.slice(0, 10);
    }

    setFormData((prev) => ({ ...prev, date_naissance: formattedValue }));

    if (formattedValue.length === 10) {
      const m = moment(formattedValue, "DD/MM/YYYY", true);
      const isValidDate = m.isValid();
      const isFutureDate = m.isAfter(moment());

      if (!isValidDate || isFutureDate) {
        setDateError(
          "La date n'est pas valide ou est une date future. Veuillez entrer une date au format JJ/MM/AAAA.",
        );
        setGroupError("");
      } else {
        setDateError("");
        setGroupError("");
      }
    }
  };

  // Photo obligatoire
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    setPhotoError("");
    setSelectedPhoto(null);
    setPhotoPreview("");

    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setPhotoError("Format d'image non supporté (jpg, png, webp).");
      return;
    }
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_IMAGE_SIZE_MB) {
      setPhotoError(`Image trop volumineuse (> ${MAX_IMAGE_SIZE_MB} Mo).`);
      return;
    }

    setSelectedPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(String(ev.target.result || ""));
    reader.readAsDataURL(file);
  };

  // Ajout d'un groupe sélectionné
  const addSelectedGroup = () => {
    if (!selectedGroupIdToAdd) return;

    const groupToAdd = groupes.find(
      (g) => g.id === Number(selectedGroupIdToAdd),
    );
    if (!groupToAdd) return;

    if (isQuotaActive && isGroupFull(groupToAdd)) {
      setGroupError(
        `Le groupe ${groupToAdd.nom} est complet (${groupToAdd.quota_groupe} places). Veuillez en choisir un autre.`,
      );
      return;
    }

    if (selectedGroups.some((g) => g.id === groupToAdd.id)) {
      setGroupError("Ce groupe est déjà ajouté.");
      return;
    }

    setSelectedGroups([...selectedGroups, groupToAdd]);
    setSelectedGroupIdToAdd("");
    setGroupError("");
  };

  const removeGroup = (id) => {
    setSelectedGroups(selectedGroups.filter((g) => g.id !== id));
  };

  // ✅ Modification 2 : affichage = tous les groupes actifs (sans filtre dojo)
  const groupesAffiches = useMemo(() => {
    return groupes.filter(
      (g) => !selectedGroups.some((sel) => sel.id === g.id),
    );
  }, [groupes, selectedGroups]);

  // ====== Submit ======
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Date requise + valide
    if (!formData.date_naissance || dateError) {
      toast.error("Veuillez corriger la date de naissance.");
      return;
    }

    // Groupes requis
    let groupsToSend = [...selectedGroups];
    if (groupsToSend.length === 0 && selectedGroupIdToAdd) {
      const g = groupes.find((gg) => gg.id === Number(selectedGroupIdToAdd));
      if (g) groupsToSend.push(g);
    }
    if (groupsToSend.length === 0) {
      toast.error("Veuillez sélectionner au moins un groupe.");
      return;
    }

    // Photo obligatoire uniquement si le droit à l'image est autorisé
    if (formData.autorisation_photo && !selectedPhoto) {
      setPhotoError(
        "La photo est obligatoire lorsque le droit à l’image est autorisé.",
      );

      toast.error(
        "Veuillez ajouter une photo de l’adhérent ou décocher le droit à l’image.",
      );

      return;
    }

    // On bloque sur une erreur photo uniquement si une photo est requise
    if (formData.autorisation_photo && photoError) {
      toast.error("Corrigez l’erreur liée à la photo.");
      return;
    }

    // Auth
    if (!user || !user.userId) {
      toast.error("Vous devez être connecté pour effectuer cette action.");
      return;
    }
    const token = user?.token || localStorage.getItem("token") || "";

    // Préparation des données
    const adherentData = {
      ...formData,
      statut_inscription: "Inscrit", // ✅ fixe
      date_naissance: validerEtFormatterDate(formData.date_naissance),
      groupes: groupsToSend.map((g) => g.id),
      saison_active: saisonActive,
      // photo_url sera gérée via upload juste après la création
    };

    try {
      // 1) Création adhérent
      const addResponse = await addNewAdherent(adherentData);
      if (!addResponse?.id) {
        toast.error(
          "Erreur lors de l'ajout de l'adhérent. Veuillez réessayer.",
        );
        return;
      }

      // 2) Association user <-> adhérent
      await assignAdherentToUser(user.userId, addResponse.id, token);

      // 3) Upload photo si le droit à l'image est autorisé
      if (formData.autorisation_photo && selectedPhoto) {
        try {
          const fd = new FormData();
          fd.append("image", selectedPhoto);

          await updateAdherentPhoto(addResponse.id, fd, token);
        } catch (err) {
          console.error("Upload photo error:", err);

          toast.error(
            "La photo n’a pas pu être enregistrée. L’inscription ne peut pas être finalisée.",
          );

          throw err;
        }
      }

      // 4) Dossier d’inscription
      await createNewDossierInscription(addResponse.id);

      // 5) Notifications
      const groupNames =
        groupsToSend.length > 0
          ? groupsToSend.map((g) => g.nom).join(", ")
          : "";
      const notifUser = `Félicitations, vous venez d'inscrire ${formData.prenom} ${formData.nom} dans le(s) groupe(s) : ${groupNames}.`;
      sendNotification(notifUser, user.userId, token);

      const notifAdmin = `${userInfo.prenom} ${userInfo.nom} vient d'inscrire ${formData.prenom} ${formData.nom} dans le(s) groupe(s) : ${groupNames}.`;
      sendNotificationToAdmin(notifAdmin, token);

      toast.success("Adhérent ajouté avec succès.");

      // Reset + navigation
      setFormData({
        nom: "",
        prenom: "",
        genre: "",
        date_naissance: "",
        lieu_naissance: "",
        licence_number: "",
        adresse: "",
        cp: "",
        commune: "",
        representant_legal: "",
        tel: "",
        tel_secondaire: "",
        email: "",
        email_secondaire: "",
        groupeId: "",
        statut_inscription: "Inscrit", // ✅ reste par défaut
        autorisation_photo: true,
        nationalite_fra: true,
        taille_tshirt: "",
        notes_adherent: "",
        saison_active: saisonActive,
        passsport_number: "",
      });
      setSelectedGroups([]);
      setSelectedGroupIdToAdd("");
      setSelectedPhoto(null);
      setPhotoPreview("");
      navigate("/dossier-d-inscription");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'ajout de l'adhérent. Veuillez réessayer.");
    }
  };

  return (
    <div className="container mx-auto pt-[150px] px-4 mb-10 font-montserrat">
      {userInfo.nom && userInfo.prenom && (
        <h2 className="text-normal italic font-normal mb-2">
          Bienvenue {userInfo.prenom} {userInfo.nom} sur la page d'inscription
          des adhérents
        </h2>
      )}
      <AdherentsMiniTable className="mt-2" />

      <h2 className="text-2xl uppercase font-bold mb-4">
        Formulaire d'Inscription pour la saison {saisonActive}
      </h2>

      {/* Image header */}
      <div className="text-center mb-4">
        <img src="/img/Team_bkm29.png" alt="Inscription" className="mx-auto" />
      </div>

      {/* Démarches d'inscription */}
      <section
        className="
    mb-6 overflow-hidden rounded-2xl
    border border-blue-200 bg-blue-50
    dark:border-blue-800 dark:bg-blue-950/30
  "
      >
        <div className="border-b border-blue-200 bg-blue-100 px-4 py-3 dark:border-blue-800 dark:bg-blue-900/40">
          <h3 className="text-base font-bold text-blue-900 dark:text-blue-100">
            Démarches pour l'inscription
          </h3>
        </div>

        <div className="px-4 py-4 md:px-5">
          <ol className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
            <li className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 font-bold text-white">
                1
              </span>

              <span className="pt-1">Remplir la page d'inscription.</span>
            </li>

            <li className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 font-bold text-white">
                2
              </span>

              <span className="pt-1">
                Télécharger, remplir et signer le formulaire FFK.
              </span>
            </li>

            <li className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 font-bold text-white">
                3
              </span>

              <span className="pt-1">
                Déposer le formulaire FFK dans la box prévue à cet effet.
              </span>
            </li>

            <li className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 font-bold text-white">
                4
              </span>

              <span className="pt-1">
                <strong>Pour les mineurs :</strong> télécharger le dossier
                complet, signer les documents, puis les déposer dans la box.
              </span>
            </li>
          </ol>
        </div>
      </section>

      <div className="text-red-500 text-sm mb-4">* : champs obligatoires</div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ÉTAPE 1 — Saison & type (fixe) */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Étape 1 — Saison & type d’inscription
          </h3>

          <div className="flex flex-wrap items-center gap-4">
            {/* Saison (verrouillée mais envoyée via hidden) */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-800 dark:text-slate-200">
                Saison<span className="text-red-500 ml-1">*</span>
              </span>
              <div
                className="inline-flex items-center rounded-lg px-3 py-1.5 min-h-[36px]
                      bg-slate-100 text-slate-900
                      dark:bg-slate-800 dark:text-slate-100
                      border border-slate-300 dark:border-slate-600"
              >
                {saisonActive ? (
                  <span className="font-medium text-sm">{saisonActive}</span>
                ) : (
                  <span className="inline-block h-4 w-24 rounded animate-pulse bg-slate-200 dark:bg-slate-700" />
                )}
              </div>
              <input
                type="hidden"
                name="saison_active"
                value={formData.saison_active}
              />
            </div>

            <div className="hidden md:block h-6 w-px bg-slate-200 dark:bg-slate-700" />

            {/* ✅ Type d’inscription fixe */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-800 dark:text-slate-200">
                Type d’inscription<span className="text-red-500 ml-1">*</span>
              </span>
              <div
                className="inline-flex items-center rounded-lg px-3 py-1.5 min-h-[36px]
                      bg-primary text-white
                      border border-primary/60"
                title="Type d’inscription fixé"
              >
                <span className="font-medium text-sm">Inscription</span>
              </div>
              {/* valeur envoyée */}
              <input type="hidden" name="statut_inscription" value="Inscrit" />
            </div>
          </div>
        </section>

        {/* ÉTAPE 2 — Groupes (multi) */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 2 — Choix du/des groupe(s)
          </h3>

          <p className="text-xs mt-1 mb-3 text-slate-600 dark:text-slate-300">
            Vous pouvez sélectionner{" "}
            <span className="font-medium">un ou plusieurs</span> groupes (tous
            dojos confondus).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end mb-4">
            {/* Groupe */}
            <div className="md:col-span-10">
              <label className="label mb-2 block text-slate-800 dark:text-slate-200">
                Groupe<span className="text-red-500 ml-1">*</span>
                <span className="text-gray-700 dark:text-gray-300 italic font-light ml-1 text-sm">
                  (un ou plusieurs groupes)
                </span>
              </label>
              <select
                value={selectedGroupIdToAdd}
                onChange={(e) => setSelectedGroupIdToAdd(e.target.value)}
                className="input w-full px-3 py-2 rounded border
                      bg-white text-slate-900
                      dark:bg-slate-800 dark:text-slate-100
                      border-slate-300 dark:border-slate-600
                      focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Sélectionnez un groupe</option>
                {groupesAffiches.map((g) => {
                  const dojoNom = dojoById[g.dojo_id]?.dojo_nom;
                  return (
                    <option key={g.id} value={g.id}>
                      {g.nom}
                      {dojoNom ? ` — ${dojoNom}` : ""}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Astuce : le nom du dojo est indiqué dans la liste (ex. “Groupe —
                Dojo”).
              </p>
            </div>

            {/* Ajouter */}
            <div className="md:col-span-2">
              <button
                type="button"
                onClick={addSelectedGroup}
                disabled={!selectedGroupIdToAdd}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 font-medium text-white
                      bg-primary hover:brightness-110 active:scale-[0.99]
                      disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                title="Ajouter le groupe sélectionné"
              >
                <FaPlus /> Je m'inscris
              </button>
            </div>
          </div>

          {selectedGroups.length === 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-700 px-4 py-3">
              ⚠️ Aucun groupe ajouté. Sélectionnez un groupe puis cliquez sur{" "}
              <span className="font-semibold">“Je m'inscris”</span>.
            </div>
          )}

          {selectedGroups.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {selectedGroups.map((g) => {
                const horaires = [
                  g.horaire1,
                  g.horaire2,
                  g.horaire3,
                  g.horaire4,
                ].filter(Boolean);
                const enseignants = [
                  g.enseignant1,
                  g.enseignant2,
                  g.enseignant3,
                  g.enseignant4,
                  g.enseignant5,
                  g.enseignant6,
                ].filter(Boolean);
                const cot = Number(g.tarif_cotisation) || 0;
                const lic = Number(g.tarif_licence) || 0;
                const total = cot + lic;
                const inscrits = Number(g.nombreAdherents) || 0;
                const quota = Number(g.quota_groupe) || 0;
                const pct =
                  quota > 0
                    ? Math.min(100, Math.round((inscrits / quota) * 100))
                    : 0;

                const borderClass = getBorderColorClass(g.color_groupe);
                const barClass = getSolidColorClass(g.color_groupe);
                const trackClass = getLightColorClass(g.color_groupe);

                return (
                  <article
                    key={g.id}
                    className={`relative rounded-2xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm hover:shadow-md transition-shadow border ${borderClass} dark:border-opacity-60`}
                  >
                    <div className="absolute inset-y-0 left-0 w-[10px]">
                      <div className={`${barClass} w-full h-full`} />
                    </div>

                    <button
                      type="button"
                      onClick={() => removeGroup(g.id)}
                      className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full w-9 h-9
                            bg-white/80 dark:bg-slate-800/80 backdrop-blur border border-slate-200 dark:border-slate-700
                            text-red-600 hover:bg-white dark:hover:bg-slate-800 transition"
                      title="Retirer ce groupe"
                    >
                      <FaTimes />
                    </button>

                    <div className="pl-[18px] pr-4 py-4">
                      <h3 className="text-lg font-semibold mb-3 bg-gradient-to-r from-slate-200 to-slate-100 dark:from-slate-800 dark:to-slate-700 text-slate-900 dark:text-slate-100 px-3 py-2 rounded-lg">
                        {g.nom}
                      </h3>

                      {/* Dojo badge */}
                      {(() => {
                        const dojo = dojoById[g.dojo_id];
                        const dojoNom = dojo?.dojo_nom || "Dojo inconnu";
                        return (
                          <div className="mb-3">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium
                                  ${trackClass} ${borderClass} dark:opacity-90`}
                            >
                              <FaMapPin className="text-[10px] opacity-80" />
                              {dojoNom}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Horaires */}
                      {horaires.length > 0 && (
                        <div className="mb-3">
                          <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 inline-flex items-center gap-2">
                            <FaClock /> Horaires
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {horaires.map((h, idx) => (
                              <span
                                key={idx}
                                className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                              >
                                {h}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Enseignants */}
                      {enseignants.length > 0 && (
                        <div className="mb-3">
                          <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 inline-flex items-center gap-2">
                            <FaUserFriends /> Enseignant(s)
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {enseignants.map((t, idx) => (
                              <span
                                key={idx}
                                className="px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-200 border border-violet-200 dark:border-violet-700"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Années de naissance */}
                      {g.annees_naissance && (
                        <div className="mb-3">
                          <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                            Années de naissance
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(Array.isArray(g.annees_naissance)
                              ? g.annees_naissance
                              : String(g.annees_naissance).split(",")
                            )
                              .map((s) => s.trim())
                              .filter(Boolean)
                              .map((an, idx) => (
                                <span
                                  key={idx}
                                  className="px-2.5 py-1 rounded-full text-xs font-medium bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-200 border border-sky-200 dark:border-sky-700"
                                >
                                  {an}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Tarifs */}
                      <div className="mb-3">
                        <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 inline-flex items-center gap-2">
                          <FaEuroSign /> Tarifs
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2">
                            <div className="text-slate-500 dark:text-slate-400">
                              Licence
                            </div>
                            <div className="font-semibold text-slate-900 dark:text-slate-100">
                              {euro(Number(g.tarif_licence))}
                            </div>
                          </div>
                          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2">
                            <div className="text-slate-500 dark:text-slate-400">
                              Cotisation
                            </div>
                            <div className="font-semibold text-slate-900 dark:text-slate-100">
                              {euro(Number(g.tarif_cotisation))}
                            </div>
                          </div>
                          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 border border-emerald-200 dark:border-emerald-700">
                            <div className="text-emerald-700 dark:text-emerald-200">
                              Total
                            </div>
                            <div className="font-semibold text-emerald-800 dark:text-emerald-100">
                              {euro(total)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Info groupe (HTML) */}
                      {g.info_groupe && (
                        <div className="mb-4">
                          <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 underline">
                            Informations Groupe
                          </div>
                          <div
                            className="prose prose-sm max-w-none dark:prose-invert text-slate-800 dark:text-slate-200 text-sm"
                            dangerouslySetInnerHTML={{ __html: g.info_groupe }}
                          />
                        </div>
                      )}

                      {/* Quota */}
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-700 dark:text-slate-300">
                            Quota:{" "}
                            <span className="font-semibold">
                              {quota || "—"}
                            </span>{" "}
                            adhérents
                          </span>
                          <span className="text-slate-700 dark:text-slate-300">
                            Inscrits:{" "}
                            <span className="font-semibold">{inscrits}</span>
                          </span>
                        </div>
                        <div
                          className={`h-2 rounded-full overflow-hidden ${trackClass} dark:opacity-80`}
                        >
                          <div
                            className={`h-2 rounded-full ${pct >= 100 ? "bg-rose-500" : barClass}`}
                            style={{ width: `${pct}%` }}
                            title={`${pct}%`}
                          />
                        </div>
                        {pct >= 100 && (
                          <p className="mt-1 text-xs text-rose-600 dark:text-rose-300">
                            Groupe complet — vous pouvez en choisir un autre.
                          </p>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {groupError && (
            <div className="mt-3 rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
              {groupError}
            </div>
          )}
        </section>

        {/* ÉTAPE 3 — Coordonnées de l’adhérent */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 3 — Coordonnées de l’adhérent
          </h3>
          <p className="text-xs mt-1 mb-3 text-slate-600 dark:text-slate-300">
            Les champs marqués d’un <span className="text-red-500">*</span> sont
            obligatoires.
          </p>

          <div className="space-y-3">
            {/* Nom */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-nom"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Nom<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-nom"
                name="nom"
                type="text"
                value={formData.nom}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            {/* Prénom */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-prenom"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Prénom<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-prenom"
                name="prenom"
                type="text"
                value={formData.prenom}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            {/* Doublon */}
            {duplicateWarning && (
              <div className="md:ml-[180px] text-xs text-rose-700 dark:text-rose-300">
                {duplicateWarning}
              </div>
            )}

            {/* Genre */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="sel-genre"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Genre<span className="text-red-500 ml-1">*</span>
              </label>
              <select
                id="sel-genre"
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border
                  bg-white text-slate-900
                  dark:bg-slate-800 dark:text-slate-100
                  border-slate-300 dark:border-slate-600
                  focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Sélectionner</option>
                <option value="Masculin">Masculin</option>
                <option value="Féminin">Féminin</option>
              </select>
            </div>

            {/* Date naissance */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-date"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Date de naissance<span className="text-red-500 ml-1">*</span>
              </label>
              <div>
                <input
                  id="inp-date"
                  name="date_naissance"
                  type="text"
                  value={formData.date_naissance}
                  onChange={handleDateChange}
                  placeholder="JJ/MM/AAAA"
                  required
                  className="input w-full max-w-[260px] px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                {dateError && (
                  <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                    {dateError}
                  </p>
                )}
              </div>
            </div>

            {/* Lieu naissance */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-lieu"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Lieu de naissance
              </label>
              <input
                id="inp-lieu"
                name="lieu_naissance"
                type="text"
                value={formData.lieu_naissance}
                onChange={handleChange}
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            {/* Adresse */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-adresse"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Adresse<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-adresse"
                name="adresse"
                type="text"
                value={formData.adresse}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            {/* CP + Commune */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-3">
              <label className="pt-2 text-sm md:text-right text-slate-800 dark:text-slate-200">
                Code postal / Commune
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="space-y-2">
                <input
                  id="inp-cp"
                  name="cp"
                  type="text"
                  inputMode="numeric"
                  value={formData.cp}
                  onChange={handleChange}
                  required
                  placeholder="Code postal (5 chiffres)"
                  className="input w-full max-w-[220px] px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />

                {communes.length > 0 ? (
                  <select
                    id="inp-commune"
                    name="commune"
                    value={formData.commune}
                    onChange={handleChange}
                    required
                    className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">
                      {communeLoading
                        ? "Chargement..."
                        : "Sélectionnez votre commune"}
                    </option>
                    {communes.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="inp-commune"
                    name="commune"
                    type="text"
                    value={formData.commune}
                    onChange={handleChange}
                    required
                    placeholder={communeLoading ? "Chargement..." : "Commune"}
                    className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                )}

                {formData.cp && formData.cp.length < 5 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Saisissez un code postal à 5 chiffres pour obtenir la liste
                    des communes.
                  </p>
                )}
              </div>
            </div>

            {/* Téléphones */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-tel"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Téléphone 1 (urgence)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-tel"
                name="tel"
                type="tel"
                inputMode="tel"
                pattern="^\+?\d{0,15}"
                value={formData.tel}
                onChange={handleChange}
                required
                placeholder="Téléphone à contacter en cas d'urgence"
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-tel2"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Téléphone secondaire
              </label>
              <input
                id="inp-tel2"
                name="tel_secondaire"
                type="tel"
                pattern="^\+?\d{0,15}"
                value={formData.tel_secondaire}
                onChange={handleChange}
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            {/* Emails */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-email"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Email<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-email2"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Email secondaire
              </label>
              <input
                id="inp-email2"
                name="email_secondaire"
                type="email"
                value={formData.email_secondaire}
                onChange={handleChange}
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            {/* Représentant légal ou contact d'urgence */}
            <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] items-center gap-3">
              <label
                htmlFor="inp-rep"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Représentant légal (pour mineur) ou personne à contacter en cas
                d'urgence + téléphone
              </label>

              <div>
                <input
                  id="inp-rep"
                  name="representant_legal"
                  type="text"
                  value={formData.representant_legal}
                  onChange={handleChange}
                  placeholder="Ex. Marie DUPONT — Mère — 06 00 00 00 00"
                  className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Indiquez le nom, le lien avec l'adhérent et le numéro de
                  téléphone.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ÉTAPE 4 — Informations complémentaires */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 4 — Informations complémentaires
          </h3>
          <div className="text-xs mt-1 mb-3 text-slate-600 dark:text-slate-300">
            Renseignez les informations additionnelles de l’adhérent.
          </div>

          <div className="space-y-3">
            {/* ✅ Modification 3 : suppression poids + ceinture + date de passage */}

            {/* N° licence */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-licence"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                N° de licence
              </label>
              <div>
                <input
                  id="inp-licence"
                  name="licence_number"
                  type="text"
                  placeholder="Laissez vide pour débutants"
                  value={formData.licence_number}
                  onChange={handleChange}
                  className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Optionnel, laissez vide si vous ne savez pas ou pour un
                  débutant.
                </p>
              </div>
            </div>

            {/* Pass'Sport */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-passsport"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                N° Pass’Sport (si éligible)
              </label>
              <input
                id="inp-passsport"
                name="passsport_number"
                type="text"
                value={formData.passsport_number}
                onChange={handleChange}
                placeholder="Saisir le numéro si applicable"
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            {/* Nationalité française */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-3">
              <label className="pt-2 text-sm md:text-right text-slate-800 dark:text-slate-200">
                Nationalité
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.nationalite_fra}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      nationalite_fra: !prev.nationalite_fra,
                    }))
                  }
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-violet-400"
                />
                <span>Nationalité française</span>
              </label>
            </div>

            {/* Taille T-Shirt */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="sel-tshirt"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Taille T-shirt
              </label>
              <select
                id="sel-tshirt"
                value={formData.taille_tshirt}
                onChange={(e) =>
                  setFormData({ ...formData, taille_tshirt: e.target.value })
                }
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Sélectionner une taille</option>
                <option value="8-12 ans">8-12 ans</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
                <option value="XXXL">XXXL</option>
              </select>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-3">
              <label
                htmlFor="txt-notes"
                className="pt-2 text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Notes
              </label>
              <textarea
                id="txt-notes"
                name="notes_adherent"
                rows={4}
                value={formData.notes_adherent}
                onChange={(e) =>
                  setFormData({ ...formData, notes_adherent: e.target.value })
                }
                placeholder="Remarques, allergies, précisions utiles…"
                className="input w-full px-3 py-2 rounded border bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </div>
        </section>

        {/* ÉTAPE 5 — Photo */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 5 — Photo de l’adhérent
            {formData.autorisation_photo && (
              <span className="text-red-500 ml-1">*</span>
            )}
          </h3>

          {formData.autorisation_photo ? (
            <>
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-3">
                La photo de l’adhérent est obligatoire pour finaliser
                l’inscription.
              </p>

              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                <span className="font-semibold">Photo obligatoire :</span> vous
                avez autorisé le droit à l’image. Une photo de l’adhérent doit
                donc être ajoutée avant de pouvoir valider l’inscription.
              </div>

              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div>
                  <input
                    className="block"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/jpg"
                    onChange={handlePhotoChange}
                    required={formData.autorisation_photo}
                  />

                  {photoError && (
                    <p className="text-rose-600 dark:text-rose-300 text-xs mt-1">
                      {photoError}
                    </p>
                  )}

                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Formats acceptés : JPG, PNG, WEBP — Taille max{" "}
                    {MAX_IMAGE_SIZE_MB} Mo.
                  </p>
                </div>

                {photoPreview && (
                  <img
                    src={photoPreview}
                    alt="Aperçu de l’adhérent"
                    className="w-24 h-24 rounded-full object-cover border border-slate-300 dark:border-slate-600"
                  />
                )}
              </div>
            </>
          ) : (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
              Vous avez refusé le droit à l’image. La photo de l’adhérent n’est
              donc pas obligatoire pour finaliser l’inscription.
            </div>
          )}
        </section>

        {/* ÉTAPE 6 — Droit à l’image & règlement intérieur */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 6 — Droit à l’image & règlement intérieur
          </h3>

          <div className="space-y-3 mt-3">
            {/* Droit à l'image */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-3">
              <label
                htmlFor="chk-photo"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Droit à l’image
              </label>
              <div className="flex items-start gap-3">
                <input
                  id="chk-photo"
                  type="checkbox"
                  checked={formData.autorisation_photo}
                  onChange={(e) => {
                    const autorisation = e.target.checked;

                    setFormData((prev) => ({
                      ...prev,
                      autorisation_photo: autorisation,
                    }));

                    // Si le droit à l'image est refusé,
                    // on supprime la photo éventuellement sélectionnée
                    if (!autorisation) {
                      setSelectedPhoto(null);
                      setPhotoPreview("");
                      setPhotoError("");
                    }
                  }}
                  className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-violet-400"
                />
                <div className="text-sm text-slate-700 dark:text-slate-200">
                  J’autorise l’association à diffuser les représentations
                  photographiques sur lesquelles pourrait apparaître l’adhérent,
                  prises lors des manifestations du club, sur supports
                  numériques et site internet, pour la durée la plus longue
                  prévue par la loi.
                  {!formData.autorisation_photo && (
                    <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                      Vous ne souhaitez pas de diffusion d’images (droit à
                      l’image refusé).
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Règlement intérieur */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-3">
              <label
                htmlFor="chk-ri"
                className="text-sm md:text-right text-slate-800 dark:text-slate-200"
              >
                Règlement intérieur
              </label>
              <div className="flex items-start gap-3">
                <input
                  id="chk-ri"
                  type="checkbox"
                  required
                  className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-violet-400"
                />
                <div className="text-sm text-slate-700 dark:text-slate-200">
                  J’ai lu et j’accepte le{" "}
                  <a
                    href="/reglement-interieur"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    règlement intérieur
                  </a>{" "}
                  du club.<span className="text-red-500 ml-1">*</span>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Cette case est obligatoire pour valider l’inscription.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Button
          text="Valider l'inscription"
          className="mx-auto mb-4"
          disabled={
            !!groupError || (formData.autorisation_photo && !!photoError)
          }
        />
      </form>

      <ToastContainer />
    </div>
  );
};

export default InscriptionPage;
